import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { X } from 'lucide-react';
import { addressSchema, type AddressFormValues } from '../../schemas/address.schema';
import { useCreateAddress, useUpdateAddress } from '../../hooks/queries/useAddresses';
import type { Address } from '../../types';
import { LocationPicker } from './LocationPicker';

interface AddressModalProps {
    isOpen: boolean;
    onClose: () => void;
    addressToEdit?: Address;
}

export function AddressModal({ isOpen, onClose, addressToEdit }: AddressModalProps) {
    const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<AddressFormValues>({
        resolver: zodResolver(addressSchema),
        defaultValues: {
            label: 'Casa',
            street: '',
            number: '',
            floor: '',
            apartment: '',
            district: '',
            city: '',
            reference: '',
            is_default: false,
            latitude: undefined,
            longitude: undefined,
        }
    });

    const handleLocationChange = async (lat: number, lng: number) => {
        setValue('latitude', lat);
        setValue('longitude', lng);

        // Reverse geocoding
        try {
            const response = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`);
            if (response.ok) {
                const data = await response.json();
                const addr = data.address;
                if (addr) {
                    if (addr.road) setValue('street', addr.road);
                    if (addr.house_number) setValue('number', addr.house_number);
                    if (addr.suburb || addr.neighbourhood) setValue('district', addr.suburb || addr.neighbourhood);
                    if (addr.city || addr.town || addr.village) setValue('city', addr.city || addr.town || addr.village);
                }
            }
        } catch (error) {
            console.error('Error in reverse geocoding:', error);
        }
    };

    const createAddress = useCreateAddress();
    const updateAddress = useUpdateAddress();

    useEffect(() => {
        if (isOpen) {
            if (addressToEdit) {
                reset({
                    label: addressToEdit.label,
                    street: addressToEdit.street,
                    number: addressToEdit.number,
                    floor: addressToEdit.floor || '',
                    apartment: addressToEdit.apartment || '',
                    district: addressToEdit.district || '',
                    city: addressToEdit.city || '',
                    reference: addressToEdit.reference || '',
                    is_default: addressToEdit.is_default,
                    latitude: addressToEdit.latitude,
                    longitude: addressToEdit.longitude,
                });
            } else {
                reset({
                    label: 'Casa',
                    street: '',
                    number: '',
                    floor: '',
                    apartment: '',
                    district: '',
                    city: '',
                    reference: '',
                    is_default: false,
                    latitude: undefined,
                    longitude: undefined,
                });
            }
        }
    }, [isOpen, addressToEdit, reset]);

    const onSubmit = async (data: AddressFormValues) => {
        console.log('[DEBUG ADDRESS MODAL] Datos a guardar:', JSON.stringify(data));
        try {
            if (addressToEdit) {
                console.log('[DEBUG ADDRESS MODAL] Actualizando dirección uuid:', addressToEdit.uuid);
                await updateAddress.mutateAsync({ id: addressToEdit.uuid, address: data });
            } else {
                console.log('[DEBUG ADDRESS MODAL] Creando nueva dirección');
                await createAddress.mutateAsync({ ...data, is_default: !!data.is_default });
            }
            onClose();
        } catch (error) {
            console.error('Error saving address:', error);
            // In a real app, show toast error here
        }
    };

    if (!isOpen) return null;

    const isLoading = createAddress.isPending || updateAddress.isPending;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-xl">
                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center sticky top-0 bg-white dark:bg-gray-800 z-10">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                        {addressToEdit ? 'Editar Dirección' : 'Nueva Dirección'}
                    </h2>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Ubicación en el mapa</label>
                        <LocationPicker
                            initialLat={watch('latitude')}
                            initialLng={watch('longitude')}
                            onChange={handleLocationChange}
                        />
                        <input type="hidden" {...register('latitude', { valueAsNumber: true })} />
                        <input type="hidden" {...register('longitude', { valueAsNumber: true })} />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Etiqueta</label>
                        <input
                            {...register('label')}
                            placeholder="Ej: Casa, Trabajo"
                            className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none transition-all"
                        />
                        {errors.label && <p className="text-red-500 text-xs mt-1">{errors.label.message}</p>}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Calle</label>
                            <input
                                {...register('street')}
                                className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none transition-all"
                            />
                            {errors.street && <p className="text-red-500 text-xs mt-1">{errors.street.message}</p>}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Altura</label>
                            <input
                                {...register('number')}
                                className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none transition-all"
                            />
                            {errors.number && <p className="text-red-500 text-xs mt-1">{errors.number.message}</p>}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Piso (Opcional)</label>
                            <input
                                {...register('floor')}
                                className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none transition-all"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Depto (Opcional)</label>
                            <input
                                {...register('apartment')}
                                className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none transition-all"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Barrio</label>
                        <input
                            {...register('district')}
                            className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none transition-all"
                        />
                        {errors.district && <p className="text-red-500 text-xs mt-1">{errors.district.message}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ciudad</label>
                        <input
                            {...register('city')}
                            className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none transition-all"
                        />
                        {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city.message}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Referencias (Opcional)</label>
                        <textarea
                            {...register('reference')}
                            rows={2}
                            className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none transition-all resize-none"
                        />
                    </div>

                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            id="is_default"
                            {...register('is_default')}
                            className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                        />
                        <label htmlFor="is_default" className="text-sm text-gray-700 dark:text-gray-300">
                            Establecer como predeterminada
                        </label>
                    </div>

                    <div className="pt-4 flex gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 py-3 px-4 rounded-xl font-semibold bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="flex-1 py-3 px-4 rounded-xl font-semibold bg-primary text-white hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isLoading ? 'Guardando...' : 'Guardar'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
